import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { Role, Permission, RoleCreateRequest, RoleUpdateRequest } from '@/types/api';
import { Plus, Edit2, Trash2, X, Shield, Check, Key, AlertTriangle, RefreshCw } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import {
  groupPermissionsByCategory,
  permissionLabel,
} from '@/utils/permissionGrouping';

export default function RolesPage() {
  const queryClient = useQueryClient();

  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canCreate = useHasPermission(PERM.ROLE_CREATE);
  const canUpdate = useHasPermission(PERM.ROLE_UPDATE);
  const canDelete = useHasPermission(PERM.ROLE_DELETE);

  // Fetch roles and permissions. includeInactive=true so a soft-deleted role
  // (and the still-attached user assignments it leaves behind) can actually be
  // found again through the UI instead of just vanishing (C1).
  const {
    data: roles = [],
    isLoading: rolesLoading,
    isError: rolesError,
    refetch: refetchRoles,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.getRoles(true),
  });

  const {
    data: permissions = [],
    isLoading: permissionsLoading,
    isError: permissionsError,
    refetch: refetchPermissions,
  } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => apiClient.getPermissions(),
  });

  const hasPageError = rolesError || permissionsError;
  const retryPageQueries = () => {
    refetchRoles();
    refetchPermissions();
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setIsModalOpen(true);
  };

  const openEditModal = (role: Role) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingRole(null);
    setIsModalOpen(false);
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (roleId: number) => apiClient.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setDeletingRole(null);
      setDeleteError(null);
    },
    onError: (err: any) => {
      setDeleteError(
        err?.response?.data?.message || err?.message || 'Failed to delete role.'
      );
    },
  });

  const groupedReference = groupPermissionsByCategory(permissions);

  if (rolesLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading roles...</div>
      </div>
    );
  }

  if (hasPageError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">
          {rolesError ? 'Failed to load roles' : 'Failed to load permissions'}
        </div>
        <button onClick={retryPageQueries} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Roles & Permissions
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage roles and their associated permissions
          </p>
        </div>

        {canCreate && (
          <button onClick={openCreateModal} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Create Role
          </button>
        )}
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div key={role.id} className={`card p-6 ${role.active === false ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Shield className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {role.displayName || role.name}
                    </h3>
                    {role.active === false && (
                      <span className="badge badge-gray text-xs">Deleted</span>
                    )}
                    {role.systemRole && (
                      <span className="badge badge-primary text-xs">System role</span>
                    )}
                  </div>
                  {role.displayName && (
                    <p className="text-xs text-gray-400">{role.name}</p>
                  )}
                  {role.description && (
                    <p className="text-sm text-gray-500">{role.description}</p>
                  )}
                </div>
              </div>
            </div>
            {role.active === false && (
              <p className="mb-4 text-xs text-amber-600">
                This role was deleted. It no longer grants any permissions and no
                longer appears on affected users' profiles.
              </p>
            )}

            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Permissions ({role.permissions?.length || 0})
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {role.permissions?.map((permission: Permission) => (
                  <div
                    key={permission.id}
                    className="flex items-center text-sm text-gray-600"
                  >
                    <Check className="h-3 w-3 text-green-500 mr-2" />
                    {permissionLabel(permission)}
                  </div>
                ))}
                {(!role.permissions || role.permissions.length === 0) && (
                  <div className="text-sm text-gray-400 italic">
                    No permissions assigned
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-2 pt-4 border-t border-gray-200">
              <button
                onClick={() => openEditModal(role)}
                className="btn btn-outline flex-1 h-9 text-sm"
                disabled={role.systemRole || role.active === false || !canUpdate}
                title={
                  role.systemRole
                    ? 'System roles cannot be edited'
                    : role.active === false
                    ? 'This role has been deleted'
                    : !canUpdate
                    ? "You don't have permission to edit roles"
                    : undefined
                }
              >
                <Edit2 className="h-3 w-3 mr-1" />
                Edit
              </button>
              <button
                onClick={() => {
                  setDeleteError(null);
                  setDeletingRole(role);
                }}
                className="btn btn-ghost flex-1 h-9 text-sm text-red-600 hover:text-red-700"
                disabled={role.systemRole || role.active === false || !canDelete}
                title={
                  role.systemRole
                    ? 'System roles cannot be deleted'
                    : role.active === false
                    ? 'This role has already been deleted'
                    : !canDelete
                    ? "You don't have permission to delete roles"
                    : undefined
                }
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {roles.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-gray-500">No roles found</p>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="btn btn-primary mt-4 h-10"
            >
              Create First Role
            </button>
          )}
        </div>
      )}

      {/* Permissions Reference */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Available Permissions
        </h3>
        <div className="space-y-6">
          {Object.entries(groupedReference).map(([category, perms]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                {category.replace(/_/g, ' ')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {perms.map((permission) => (
                  <div
                    key={permission.id}
                    className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <Key className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {permissionLabel(permission)}
                      </div>
                      <code className="text-xs text-gray-400">
                        {permission.name}
                      </code>
                      {permission.description && (
                        <div className="text-xs text-gray-500 mt-1">
                          {permission.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <RoleModal
          role={editingRole}
          permissions={permissions}
          onClose={closeModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            closeModal();
          }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deletingRole && (
        <Modal isOpen onClose={() => setDeletingRole(null)} size="sm">
          <ModalHeader onClose={() => setDeletingRole(null)}>
            Delete Role
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete{' '}
              <strong>{deletingRole.displayName || deletingRole.name}</strong>?
              Users assigned this role will lose its permissions.
            </p>
            {deleteError && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {deleteError}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              onClick={() => setDeletingRole(null)}
              className="btn btn-outline"
              disabled={deleteMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => deleteMutation.mutate(deletingRole.id)}
              className="btn bg-red-600 text-white hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

// Role Modal Component
interface RoleModalProps {
  role: Role | null;
  permissions: Permission[];
  onClose: () => void;
  onSuccess: () => void;
}

function RoleModal({ role, permissions, onClose, onSuccess }: RoleModalProps) {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    displayName: role?.displayName || '',
    description: role?.description || '',
    roleLevel: role?.roleLevel || 'RESTAURANT',
    permissionIds: role?.permissions?.map((p) => p.id) || [],
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [nameTaken, setNameTaken] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: RoleCreateRequest) => apiClient.createRole(data),
    onSuccess,
  });

  const updateMutation = useMutation({
    mutationFn: (data: RoleUpdateRequest) => apiClient.updateRole(role!.id, data),
    onSuccess,
  });

  const checkNameAvailability = async () => {
    const name = formData.name;
    if (!name || name.length < 3 || (role && name === role.name)) {
      setNameTaken(false);
      return;
    }
    try {
      setNameTaken(await apiClient.checkRoleNameExists(name));
    } catch {
      setNameTaken(false); // backend re-validates on submit anyway
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (nameTaken) {
      return;
    }

    try {
      if (role) {
        // roleLevel is immutable after creation (RoleUpdateRequest has no such
        // field), so it's deliberately left out of the update payload.
        const { roleLevel: _roleLevel, ...updateData } = formData;
        await updateMutation.mutateAsync({
          ...updateData,
          displayName: formData.displayName || undefined,
        });
      } else {
        await createMutation.mutateAsync({
          ...formData,
          displayName: formData.displayName || undefined,
        });
      }
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save role. Please try again.'
      );
    }
  };

  // `permissions` is the active-only list fetched by the parent. A permission
  // that's been soft-deleted while still attached to *this* role would
  // otherwise have no checkbox anywhere -- invisible, yet every Save still
  // round-trips its ID right back via permissionIds (M2). Merge it back in
  // (its own category, flagged inactive) so it can be seen and, via the
  // existing per-category select-all, intentionally removed.
  const activePermissionIds = new Set(permissions.map((p) => p.id));
  const retainedInactivePermissions = (role?.permissions ?? []).filter(
    (p) => !activePermissionIds.has(p.id)
  );
  const groupedPermissions = groupPermissionsByCategory([
    ...permissions,
    ...retainedInactivePermissions,
  ]);

  const toggleCategory = (perms: Permission[], checked: boolean) => {
    const ids = perms.map((p) => p.id);
    setFormData((prev) => ({
      ...prev,
      permissionIds: checked
        ? Array.from(new Set([...prev.permissionIds, ...ids]))
        : prev.permissionIds.filter((id) => !ids.includes(id)),
    }));
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-3xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {role ? 'Edit Role' : 'Create Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="role-name" className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <input
              id="role-name"
              type="text"
              value={formData.name}
              onChange={(e) => {
                setNameTaken(false);
                setFormData({
                  ...formData,
                  name: e.target.value.toUpperCase().replace(/\s+/g, '_'),
                });
              }}
              onBlur={checkNameAvailability}
              className="input w-full"
              placeholder="SHIFT_SUPERVISOR"
              required
              minLength={3}
              maxLength={100}
              pattern="[A-Z][A-Z0-9_]*"
              title="Uppercase letters, digits and underscores"
            />
            {nameTaken && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                A role with this name already exists.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="role-display-name" className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <input
              id="role-display-name"
              type="text"
              value={formData.displayName}
              onChange={(e) =>
                setFormData({ ...formData, displayName: e.target.value })
              }
              className="input w-full"
              placeholder="Shift Supervisor"
              maxLength={100}
            />
          </div>

          <div>
            <label htmlFor="role-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="role-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="input w-full"
              rows={2}
              maxLength={255}
              placeholder="Brief description of this role"
            />
          </div>

          <div>
            <label htmlFor="role-level" className="block text-sm font-medium text-gray-700 mb-1">
              Role Level
            </label>
            <select
              id="role-level"
              value={formData.roleLevel}
              onChange={(e) =>
                setFormData({ ...formData, roleLevel: e.target.value as typeof formData.roleLevel })
              }
              className="input w-full"
              disabled={!!role}
            >
              <option value="PLATFORM">Platform</option>
              <option value="GROUP">Group</option>
              <option value="RESTAURANT">Restaurant</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {role
                ? 'Role level cannot be changed after creation.'
                : "Scope this role is intended for. Doesn't restrict which context an admin can assign it to on the Users page -- it's descriptive metadata only."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permissions
            </label>

            <div className="space-y-4">
              {Object.entries(groupedPermissions).map(([category, perms]) => {
                const selectedCount = perms.filter((p) =>
                  formData.permissionIds.includes(p.id)
                ).length;
                const allSelected = selectedCount === perms.length;
                const someSelected = selectedCount > 0 && !allSelected;
                return (
                <div key={category} className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-center justify-between mb-3 cursor-pointer">
                    <span className="flex items-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={(e) => toggleCategory(perms, e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label={`Select all ${category.replace(/_/g, ' ')} permissions`}
                      />
                      <h4 className="ml-2 text-sm font-semibold text-gray-900">
                        {category.replace(/_/g, ' ')}
                      </h4>
                    </span>
                    <span className="text-xs text-gray-400">
                      {selectedCount}/{perms.length}
                    </span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {perms.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-start cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.permissionIds.includes(permission.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                permissionIds: [
                                  ...formData.permissionIds,
                                  permission.id,
                                ],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                permissionIds: formData.permissionIds.filter(
                                  (id) => id !== permission.id
                                ),
                              });
                            }
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                        />
                        <div className="ml-2">
                          <span className="text-sm text-gray-700">
                            {permissionLabel(permission)}
                            {!activePermissionIds.has(permission.id) && (
                              <span className="text-amber-600"> (inactive)</span>
                            )}
                          </span>
                          {permission.description && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {permission.description}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {submitError && (
            <p className="text-sm text-red-600" role="alert">
              {submitError}
            </p>
          )}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : role
                ? 'Update Role'
                : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
